// Metronom-runde (Forslag D) — rytme-træning.
//
// Klassisk: skriv fast tekst én tast pr. tik.
// Overlevelse: fortsæt så længe uden fejl og inden for takten — rekord = tid.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardLayoutId,
  MetronomeMode,
  RoundPresentation,
  RoundResult,
  Trin,
  UserProfile,
} from '../../types';
import type { PreparedRound } from '../../lib/lessonGenerator';
import {
  appendMetronomeSurvivalSnippet,
  buildMetronomeWordPool,
  formatSurvivalMs,
  isOnBeat,
  metronomeSurvivalRecordMs,
  METRONOME_RHYTHM_TOLERANCE_MS,
  METRONOME_SURVIVAL_APPEND_THRESHOLD,
  METRONOME_SURVIVAL_OFF_BEAT_LIMIT,
  rhythmDistanceFromBeat,
} from '../../lib/metronomeSurvival';
import { clampMetronomeBpm } from '../../lib/presentationTempo';
import { defaultPreferences } from '../../lib/storage';
import { useRoundInput } from '../../lib/useRoundInput';
import Keyboard from '../Keyboard';

interface MetronomeRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  user: UserProfile;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  /** When false, survival mode is disabled (curriculum metronome). */
  allowSurvivalMode?: boolean;
  keyboardLayoutId?: KeyboardLayoutId;
  onFinish: (r: RoundResult) => void;
}

type SurvivalEndReason = NonNullable<RoundResult['survivalEndReason']>;

interface SurvivalMeta {
  reason: SurvivalEndReason;
  mistakeExpected?: string;
  mistakeTyped?: string;
  mistakeAt?: number;
  offBeatStreak?: number;
  lastOffBeatMs?: number;
  rhythmKind?: 'timing' | 'missed';
}

const COUNTDOWN_LABELS = ['3', '2', '1'] as const;

export default function MetronomeRound({
  round,
  trin,
  keyboardLayout,
  user,
  strictCase = false,
  allowAltLayer = false,
  allowSurvivalMode = true,
  keyboardLayoutId,
  onFinish,
}: MetronomeRoundProps) {
  const initialText = round.targetText;
  const mode = round.template.mode;
  const presentation = round.template.presentation as Extract<
    RoundPresentation,
    { kind: 'metronome' }
  >;
  const bpm = clampMetronomeBpm(presentation?.bpm ?? 60);
  const beatMs = 60_000 / bpm;

  const prefs = user.preferences ?? defaultPreferences();
  const metronomeMode: MetronomeMode = allowSurvivalMode
    ? (prefs.metronomeMode ?? 'classic')
    : 'classic';
  const survivalMode = metronomeMode === 'survival';
  const audibleTick = prefs.audioEnabled && prefs.metronomeEnabled;

  const wordPool = useMemo(
    () => buildMetronomeWordPool(initialText),
    [initialText],
  );
  const [liveText, setLiveText] = useState(initialText);
  const targetText = survivalMode ? liveText : initialText;

  const audioCtxRef = useRef<AudioContext | null>(null);
  const beatStartRef = useRef<number>(0);
  const lastTickAtRef = useRef<number>(0);
  const beatIndexRef = useRef(0);
  const consecutiveOffBeatRef = useRef(0);
  const typedSinceLastTickRef = useRef(false);
  const survivalMetaRef = useRef<SurvivalMeta>({ reason: 'escape' });
  const finishRoundRef = useRef<(completed: boolean) => void>(() => {});
  const elapsedMsRef = useRef(0);

  const [pulse, setPulse] = useState(0);
  const [rhythmFeedback, setRhythmFeedback] = useState<
    'on' | 'off' | 'missed' | 'pending' | null
  >(null);
  const [offBeatStreak, setOffBeatStreak] = useState(0);
  const onbeatCountRef = useRef({ onBeat: 0, total: 0 });
  const [countdownLabel, setCountdownLabel] = useState<
    (typeof COUNTDOWN_LABELS)[number] | 'start' | null
  >('3');
  const [typingEnabled, setTypingEnabled] = useState(false);

  const recordMs = metronomeSurvivalRecordMs(user);

  const ensureAudioContext = useCallback(() => {
    if (audioCtxRef.current) return audioCtxRef.current;
    if (typeof window === 'undefined') return null;
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      void ctx.resume();
    } catch {
      audioCtxRef.current = null;
    }
    return audioCtxRef.current;
  }, []);

  const applyOffBeatRef = useRef<
    (opts: { distance?: number; kind: 'timing' | 'missed' }) => void
  >(() => {});
  const checkMissedBeatRef = useRef<() => void>(() => {});

  const tick = useCallback(() => {
    setPulse((p) => p + 1);
    if (!audibleTick) return;
    const ctx = ensureAudioContext();
    if (!ctx) return;
    void ctx.resume().catch(() => {});
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.07);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch {
      /* ignore audio errors */
    }
  }, [audibleTick, ensureAudioContext]);

  const applyOffBeat = useCallback(
    (opts: { distance?: number; kind: 'timing' | 'missed' }) => {
      onbeatCountRef.current.total += 1;
      const streak = consecutiveOffBeatRef.current + 1;
      consecutiveOffBeatRef.current = streak;
      setOffBeatStreak(streak);
      setRhythmFeedback(opts.kind === 'missed' ? 'missed' : 'off');

      if (survivalMode && streak >= METRONOME_SURVIVAL_OFF_BEAT_LIMIT) {
        survivalMetaRef.current = {
          reason: 'rhythm',
          offBeatStreak: streak,
          lastOffBeatMs: opts.distance,
          rhythmKind: opts.kind,
        };
        finishRoundRef.current(false);
      }
      window.setTimeout(() => setRhythmFeedback(null), 250);
    },
    [survivalMode],
  );

  applyOffBeatRef.current = applyOffBeat;

  const checkMissedBeat = useCallback(() => {
    if (!typedSinceLastTickRef.current) {
      applyOffBeatRef.current({ kind: 'missed' });
    }
    typedSinceLastTickRef.current = false;
  }, []);

  checkMissedBeatRef.current = checkMissedBeat;

  useEffect(() => {
    if (audibleTick) ensureAudioContext();

    beatIndexRef.current = 0;
    beatStartRef.current = 0;
    lastTickAtRef.current = 0;
    typedSinceLastTickRef.current = false;
    consecutiveOffBeatRef.current = 0;
    setOffBeatStreak(0);
    setTypingEnabled(false);
    setCountdownLabel('3');

    const onBeat = () => {
      const i = beatIndexRef.current;
      const now = Date.now();
      lastTickAtRef.current = now;
      tick();

      if (i > COUNTDOWN_LABELS.length) {
        checkMissedBeatRef.current();
      }

      if (i < COUNTDOWN_LABELS.length) {
        setCountdownLabel(COUNTDOWN_LABELS[i]);
      } else if (i === COUNTDOWN_LABELS.length) {
        setCountdownLabel('start');
        beatStartRef.current = now;
        setTypingEnabled(true);
        typedSinceLastTickRef.current = false;
        window.setTimeout(
          () => setCountdownLabel(null),
          Math.min(400, beatMs / 2),
        );
      }

      beatIndexRef.current = i + 1;
    };

    onBeat();
    const interval = window.setInterval(onBeat, beatMs);
    return () => window.clearInterval(interval);
  }, [beatMs, audibleTick, ensureAudioContext, tick]);

  const appendMoreText = useCallback(() => {
    setLiveText((prev) =>
      prev + appendMetronomeSurvivalSnippet(wordPool, prev.length > 0),
    );
  }, [wordPool]);

  const evaluateRhythm = useCallback(() => {
    typedSinceLastTickRef.current = true;

    const lastTick = lastTickAtRef.current;
    if (lastTick === 0) return;

    const now = Date.now();
    const distance = rhythmDistanceFromBeat(now, lastTick, beatMs);

    if (isOnBeat(now, lastTick, beatMs)) {
      onbeatCountRef.current.onBeat += 1;
      onbeatCountRef.current.total += 1;
      consecutiveOffBeatRef.current = 0;
      setOffBeatStreak(0);
      setRhythmFeedback('on');
      window.setTimeout(() => setRhythmFeedback(null), 250);
    } else {
      applyOffBeatRef.current({ distance, kind: 'timing' });
    }
  }, [beatMs]);

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'metronome',
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayoutId ?? keyboardLayout,
    onFinish,
    enabled: typingEnabled,
    continueOnComplete: survivalMode,
    failOnFirstMistake: survivalMode,
    onMistake: survivalMode
      ? ({ typed, expected, cursor }) => {
          survivalMetaRef.current = {
            reason: 'mistake',
            mistakeExpected: expected,
            mistakeTyped: typed,
            mistakeAt: cursor,
          };
        }
      : undefined,
    buildResultExtras: survivalMode
      ? () => {
          const meta = survivalMetaRef.current;
          return {
            survivalMs: elapsedMsRef.current,
            survivalEndReason: meta.reason,
            metronomeMode: 'survival' as const,
            survivalMistakeExpected: meta.mistakeExpected,
            survivalMistakeTyped: meta.mistakeTyped,
            survivalMistakeAt: meta.mistakeAt,
            survivalOffBeatStreak: meta.offBeatStreak,
            survivalLastOffBeatMs: meta.lastOffBeatMs,
            survivalRhythmKind: meta.rhythmKind,
          };
        }
      : undefined,
    onAdvance: ({ completed }) => {
      if (completed) return;
      evaluateRhythm();
    },
  });

  finishRoundRef.current = input.finish;
  elapsedMsRef.current = input.elapsedMs;

  useEffect(() => {
    if (!survivalMode || !typingEnabled || input.finished) return;
    if (input.cursor >= liveText.length - METRONOME_SURVIVAL_APPEND_THRESHOLD) {
      appendMoreText();
    }
  }, [
    input.cursor,
    liveText.length,
    survivalMode,
    typingEnabled,
    input.finished,
    appendMoreText,
  ]);

  void input.totalKeystrokes;
  const { onBeat: onBeatCount, total: rhythmTotal } = onbeatCountRef.current;
  const onBeatPct =
    rhythmTotal === 0 ? 0 : (onBeatCount / rhythmTotal) * 100;

  const toleranceLabel = `${METRONOME_RHYTHM_TOLERANCE_MS} ms`;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={survivalMode ? 'Overlevet' : 'Tid'}
          value={
            survivalMode
              ? formatSurvivalMs(input.elapsedMs)
              : formatMs(input.elapsedMs)
          }
        />
        {survivalMode && (
          <Stat
            label="Rekord"
            value={recordMs > 0 ? formatSurvivalMs(recordMs) : '—'}
          />
        )}
        {!survivalMode && <Stat label="WPM" value={input.liveWpm.toFixed(0)} />}
        <Stat
          label={survivalMode ? 'På takt' : 'Rytme'}
          value={`${onBeatPct.toFixed(0)}%`}
        />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${targetText.length}`}
        />
      </div>

      <div className="surface flex flex-col items-center gap-4 p-6">
        <div className="flex items-center gap-6">
          <PulseIndicator pulseKey={pulse} bpm={bpm} />
          <div className="text-center">
            <p className="label">
              Metronom · {bpm} BPM
              {survivalMode ? ' · Overlevelse' : ''}
            </p>
            <p className="mt-1 text-sm text-muted">
              {survivalMode
                ? 'Ingen fejl — én tast pr. tik inden for ±' +
                  toleranceLabel +
                  '. Teksten fortsætter.'
                : audibleTick
                  ? 'Skriv én tast pr. tik. Du må ikke gå hurtigere.'
                  : 'Lyd er slået fra — følg den blå puls.'}
            </p>
          </div>
        </div>
        <RhythmFeedback
          feedback={rhythmFeedback}
          survival={survivalMode}
          offBeatStreak={offBeatStreak}
          offBeatLimit={METRONOME_SURVIVAL_OFF_BEAT_LIMIT}
        />
        {countdownLabel && (
          <p className="font-mono text-4xl font-bold tabular-nums text-indigo-600 dark:text-indigo-300 animate-fade-in-up">
            {countdownLabel === 'start' ? 'Start!' : countdownLabel}
          </p>
        )}
      </div>

      <TextDisplay
        text={targetText}
        cursor={input.cursor}
        correctMask={input.correctMask}
      />

      <Keyboard
        layoutId={keyboardLayout}
        nextExpectedKey={input.expectedChar}
        lastPressedKey={input.lastPress?.key}
        lastPressCorrect={input.lastPress?.correct}
        unlockedKeys={trin.unlockedKeys}
        hintLevel={input.hintLevel}
        showFingerDot={input.hintLevel !== 'none'}
      />

      <p className="text-center text-xs text-faint">
        {typingEnabled
          ? survivalMode
            ? `Én tekstfejl eller ${METRONOME_SURVIVAL_OFF_BEAT_LIMIT} missede/off-beat tiks i træk afslutter. Escape = stop.`
            : `Tryk Escape for at afslutte runden tidligt. Mål: ramme tasten inden for ${toleranceLabel} af hver tik.`
          : '3–2–1, så Start! på næste tik — første tast samtidig med Start!'}
      </p>
    </>
  );
}

function PulseIndicator({ pulseKey, bpm }: { pulseKey: number; bpm: number }) {
  const beatMs = 60_000 / bpm;
  return (
    <div
      key={pulseKey}
      className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
      style={{
        animation: `metronome-pulse ${Math.min(0.25, beatMs / 1000 / 2)}s ease-out`,
      }}
    >
      <span className="text-xs font-mono">♪</span>
    </div>
  );
}

function RhythmFeedback({
  feedback,
  survival,
  offBeatStreak,
  offBeatLimit,
}: {
  feedback: 'on' | 'off' | 'missed' | 'pending' | null;
  survival?: boolean;
  offBeatStreak: number;
  offBeatLimit: number;
}) {
  if (!feedback) return <div className="h-5" />;
  if (feedback === 'on')
    return (
      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-300 animate-fade-in-up">
        På takten
      </p>
    );
  if (feedback === 'missed')
    return (
      <p className="text-sm font-semibold text-amber-600 dark:text-amber-300 animate-fade-in-up">
        {survival
          ? `Ingen tast på tikken (${offBeatStreak}/${offBeatLimit})`
          : 'Ingen tast på tikken'}
      </p>
    );
  return (
    <p className="text-sm font-semibold text-amber-600 dark:text-amber-300 animate-fade-in-up">
      {survival
        ? `Ude af takt (${offBeatStreak}/${offBeatLimit}) — skriv på næste tik`
        : 'Ude af takt'}
    </p>
  );
}

function TextDisplay({
  text,
  cursor,
  correctMask,
}: {
  text: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
}) {
  const windowSize = 240;
  const start = Math.max(0, cursor - 40);
  const end = Math.min(text.length, start + windowSize);
  const slice = text.slice(start, end);

  return (
    <div className="surface p-6">
      <p className="whitespace-pre-wrap break-words font-mono text-2xl leading-relaxed tracking-wide">
        {[...slice].map((ch, i) => {
          const absoluteIndex = start + i;
          const isCorrect = correctMask[absoluteIndex];
          const isCursor = absoluteIndex === cursor;
          let cls = 'text-zinc-400 dark:text-zinc-500';
          if (isCorrect) cls = 'text-emerald-600 dark:text-emerald-300';
          else if (isCursor)
            cls =
              'text-zinc-900 dark:text-zinc-100 bg-indigo-100 dark:bg-indigo-500/10 rounded-sm border-l-2 border-indigo-500';
          return (
            <span key={absoluteIndex} className={cls}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          );
        })}
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
